import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { mitErsatz, pruefeMitMakro } from "@/office/makro/pruefung";
import { readFields } from "@/office/word/layout";
import { readFormenMitUmbruch } from "@/office/word/media";
import { MAKRO_DOKUMENT, MAKRO_INITIALE, VBA_FORMTYP } from "../makros";

export const lektionTextfelder: Lesson = {
  id: "textfelder-initiale",
  title: "Textfelder, Initialen und Datumsfelder",
  summary: "Text, der frei auf der Seite steht, ein großer Anfangsbuchstabe und ein Datum, das sich selbst pflegt.",
  durationMinutes: 10,
  learningGoals: [
    "Ein Textfeld für einen Randhinweis oder ein Zitat einsetzen",
    "Eine Initiale über Einfügen → Initiale setzen",
    "Datum und Uhrzeit als Feld einfügen",
  ],
  steps: [
    {
      id: "textfeld",
      title: "Ein Textfeld",
      explanation: [
        "Ein Textfeld ist ein Rahmen, der frei auf der Seite liegt und eigenen Text enthält. " +
          "Typische Einsätze sind ein hervorgehobenes Zitat, ein Randhinweis oder ein " +
          "Kasten neben dem Fließtext. Du findest es unter „Einfügen“ → „Textfeld“; der " +
          "Katalog bietet fertige Varianten, „Textfeld erstellen“ zeichnet ein leeres.",
        "Weil ein Textfeld außerhalb des Textflusses steht, gilt dasselbe wie bei Bildern: " +
          "Der Textumbruch bestimmt, wie der Fließtext darum herumläuft, und der Anker " +
          "bestimmt, zu welchem Absatz es gehört (siehe Modul „Bilder“).",
        "Und eine Warnung: Screenreader lesen Textfelder oft an einer unerwarteten Stelle " +
          "oder gar nicht. Was zum eigentlichen Inhalt gehört, steht deshalb im Fließtext – " +
          "das Textfeld wiederholt oder ergänzt nur.",
      ],
      keyPoints: [
        "Einfügen → Textfeld → Katalog oder „Textfeld erstellen“.",
        "Textumbruch und Anker wie bei Bildern.",
        "Wichtiger Inhalt gehört in den Fließtext, nicht nur ins Textfeld.",
      ],
      task: {
        instruction: "Füge ein Textfeld mit einem kurzen Zitat oder Hinweis in dein Dokument ein.",
        hint: "Einfügen → Textfeld → eine Variante aus dem Katalog wählen und den Beispieltext ersetzen.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => {
              const formen = await readFormenMitUmbruch();
              return formen ? formen.filter((form) => form.type === "TextBox").length : null;
            },
            MAKRO_DOKUMENT,
            (bericht) =>
              [...bericht.gruppe("form").values()].filter(
                (form) => VBA_FORMTYP[Number(form.get("typ"))] === "TextBox",
              ).length,
            (anzahl) =>
              fromCriteria([criterion(`Textfelder im Dokument: ${anzahl}`, anzahl > 0)], {
                success: "Das Textfeld steht.",
                failure: "Im Dokument ist noch kein Textfeld.",
                hint: "Eine Form mit Text ist kein Textfeld – nutze Einfügen → Textfeld.",
              }),
          ),
      },
    },
    {
      id: "initiale",
      title: "Eine Initiale",
      explanation: [
        "Eine Initiale ist der vergrößerte erste Buchstabe eines Absatzes, der über mehrere " +
          "Zeilen reicht – bekannt aus Büchern und Zeitschriften. „Einfügen“ → „Initiale“ → " +
          "„Initialoptionen“ legt fest, ob sie im Text oder im Rand steht, über wie viele " +
          "Zeilen sie reicht und welche Schrift sie bekommt.",
        "Drei Zeilen sind ein guter Wert. Mehr wirkt schnell wuchtig, weniger geht im Text " +
          "unter. Und wie bei allen Schmuckelementen gilt: einmal pro Kapitel, nicht in " +
          "jedem Absatz.",
      ],
      keyPoints: [
        "Einfügen → Initiale → Initialoptionen.",
        "Zwei bis drei Zeilen hoch, einmal pro Kapitel.",
      ],
      task: {
        instruction: "Gib dem ersten Absatz nach einer Überschrift eine Initiale über zwei bis vier Zeilen.",
        hint: "Cursor in den Absatz, Einfügen → Initiale → Initialoptionen → „Im Text“, „Initialhöhe“ 3.",
        macro: MAKRO_INITIALE,
        check: () =>
          pruefeMitMakro(MAKRO_INITIALE, (bericht) => {
            const initialen = bericht.zahl("initialen") ?? 0;
            const zeilen = bericht.zahl("zeilen") ?? 0;
            return fromCriteria(
              [
                criterion(`Eine Initiale ist gesetzt (gefunden: ${initialen})`, initialen > 0),
                criterion(`Sie reicht über zwei bis vier Zeilen (eingestellt: ${zeilen})`, zeilen >= 2 && zeilen <= 4),
              ],
              {
                success: "Die Initiale sitzt – ein ruhiger, klassischer Einstieg ins Kapitel.",
                failure: "Die Initiale fehlt noch oder hat eine unpassende Höhe.",
                hint: "Ein von Hand vergrößerter Buchstabe ist keine Initiale – er verschiebt die erste Zeile.",
              },
            );
          }),
      },
    },
    {
      id: "datumsfeld",
      title: "Datum und Uhrzeit als Feld",
      explanation: [
        "„Einfügen“ → „Datum und Uhrzeit“ bietet eine Liste von Formaten. Entscheidend ist " +
          "das Häkchen „Automatisch aktualisieren“: Ist es gesetzt, fügt Word ein Feld ein, " +
          "das immer das aktuelle Datum zeigt – praktisch für Vorlagen, aber tückisch bei " +
          "Briefen, die ein Jahr später geöffnet werden.",
        "Für Briefe ist deshalb das Erstelldatum (Feld „CreateDate“) oft die bessere Wahl; " +
          "für Protokolle das Speicherdatum („SaveDate“). Beide fügst du über „Einfügen“ → " +
          "„Schnellbausteine“ → „Feld“ ein.",
      ],
      keyPoints: [
        "Einfügen → Datum und Uhrzeit → „Automatisch aktualisieren“.",
        "DATE = heute, CREATEDATE = Erstellung, SAVEDATE = letzte Speicherung.",
      ],
      task: {
        instruction: "Füge ein Datum als Feld ein – aktuelles Datum, Erstell- oder Speicherdatum.",
        hint: "Einfügen → Datum und Uhrzeit → Format wählen → Häkchen „Automatisch aktualisieren“.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung (ab WordApi 1.4).",
              "Mit Alt + F9 zeigt sich ein Datumsfeld als { DATE … }. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          const datum = felder.filter((feld) => /^(DATE|CREATEDATE|SAVEDATE|PRINTDATE|TIME)\b/i.test(feld.code));
          return fromCriteria([criterion(`Datumsfelder im Dokument: ${datum.length}`, datum.length > 0)], {
            success: "Das Datum pflegt sich jetzt selbst.",
            failure: "Im Dokument steht noch kein Datumsfeld.",
            hint: "Ohne das Häkchen „Automatisch aktualisieren“ fügt Word nur Text ein, kein Feld.",
          });
        },
      },
    },
  ],
};
